import { useTranslation } from "react-i18next"

import { TiktokProfileEmbed } from "@/components/candidates/tiktok-profile-embed"
import { TiktokProfileFallback } from "@/components/candidates/tiktok-profile-fallback"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import type { TiktokProfileEmbed as TiktokProfileEmbedData } from "@/types/candidates"

/**
 * OKS-628 — "TikTok profile" section on the candidate detail page.
 *
 * Hosts the profile embed under the profile card (left column, UX Spec §2)
 * as a labelled sub-section. Chooses between the hydrating embed (valid
 * oEmbed `html`) and the single graceful fallback block (any non-success
 * state — `html === null`). The section label is a real heading so
 * screen-reader users understand the region (UX Spec §6).
 *
 * Rendered inside an Inertia `<Deferred>` on the page — see
 * {@link TiktokProfileSectionSkeleton} for the first-paint fallback.
 *
 * Locked testid: `candidate-detail-tiktok-profile` (section root).
 */
interface TiktokProfileSectionProps {
  embed: TiktokProfileEmbedData
  handle: string
}

export function TiktokProfileSection({
  embed,
  handle,
}: TiktokProfileSectionProps) {
  const { t } = useTranslation()

  return (
    <Card data-testid="candidate-detail-tiktok-profile">
      <CardContent className="flex flex-col gap-3 pt-6">
        <h2 className="text-muted-foreground text-sm font-medium">
          {t("candidates.detail.tiktok_profile.label")}
        </h2>
        {embed.html ? (
          <TiktokProfileEmbed html={embed.html} handle={handle} />
        ) : (
          <TiktokProfileFallback handle={handle} />
        )}
      </CardContent>
    </Card>
  )
}

/**
 * First-paint / deferred-loading skeleton for the section.
 *
 * Reserves the shared `min-h-[220px]` floor that BOTH resolved states also
 * carry — `TiktokProfileEmbed`'s container and (since OKS-1007)
 * `TiktokProfileFallback`'s root — so the deferred swap itself shifts nothing,
 * whichever branch resolves.
 *
 * ⚠️ This is NOT "zero cumulative layout shift". It says nothing about what
 * happens AFTER the swap: `TiktokProfileEmbed` has no upper bound (capping
 * would crop a live embed), so TikTok's `embed.js` injecting its `<iframe>`
 * grows the section past this floor and moves the column a second time.
 * OKS-1015 protects a press against that growth — the embed freezes its own
 * height for the press — but a shift that ends before the press is still
 * ordinary layout shift.
 *
 * The earlier wording covered only the embed arm ("the resolved embed"), which
 * is precisely why the fallback arm was allowed to drift out of agreement and
 * cost OKS-1007. Keep the floor named in all three places or it will drift
 * again.
 */
export function TiktokProfileSectionSkeleton() {
  const { t } = useTranslation()

  return (
    <Card data-testid="candidate-detail-tiktok-profile">
      <CardContent className="flex flex-col gap-3 pt-6">
        <h2 className="text-muted-foreground text-sm font-medium">
          {t("candidates.detail.tiktok_profile.label")}
        </h2>
        <Skeleton
          data-testid="candidate-detail-tiktok-profile-skeleton"
          aria-label={t("candidates.detail.tiktok_profile.loading_aria")}
          className="mx-auto min-h-[220px] w-full max-w-[420px] rounded-xl"
        />
      </CardContent>
    </Card>
  )
}
